import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  const getApiScope = () => {
    if (window.APP_CONFIG && window.APP_CONFIG.REACT_APP_API_SCOPE) {
      return window.APP_CONFIG.REACT_APP_API_SCOPE;
    }
    return process.env.REACT_APP_API_SCOPE;
  };
  
  const loginScopes = ["openid", "profile", "email", getApiScope()];

  // This effect runs once on mount to initialize
  useEffect(() => {
    console.log('AuthContext - MSAL state on init:', { 
      inProgress, 
      isAuthenticated, 
      accountsExist: accounts.length > 0 
    });
    
    // Allow MSAL time to fully process any cached tokens
    const initTimer = setTimeout(() => {
      console.log('AuthContext - Setting authInitialized to true');
      setAuthInitialized(true);
    }, 1500); // Increased delay for better reliability
    
    return () => clearTimeout(initTimer);
  }, []);

  // This effect runs whenever authentication state changes
  useEffect(() => {
    // Only consider loading complete when both MSAL inProgress is "none" AND we've allowed
    // time for auth initialization
    const loadingState = inProgress !== "none" || !authInitialized;
    console.log('AuthContext - Updating state:', { 
      loadingState, 
      inProgress, 
      authInitialized,
      isAuthenticated,
      accountsExist: accounts.length > 0 
    });
    
    setIsLoading(loadingState);
    
    // Consider having accounts as sufficient for user data, even if isAuthenticated isn't true yet
    if (accounts.length > 0) {
      setUser({
        name: accounts[0].name,
        username: accounts[0].username,
        email: accounts[0].username,
        picture: null // Azure AD doesn't provide a picture by default
      });
    } else {
      setUser(null);
    }
  }, [isAuthenticated, accounts, inProgress, authInitialized]);

  const login = async () => {
    try {
      await instance.loginRedirect({
        scopes: loginScopes,
        prompt: "select_account"
      });
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  const logout = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const getAccessToken = async () => {
    if (!accounts || accounts.length === 0) {
      throw new Error("No accounts available");
    }

    const tokenRequest = {
      scopes: [getApiScope()],
      account: accounts[0]
    };

    try {
      //console.log('Getting token with scope:', getApiScope());
      const response = await instance.acquireTokenSilent(tokenRequest);
      //console.log('Token acquired successfully');
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await instance.acquireTokenRedirect(tokenRequest);
          return response.accessToken;
        } catch (err) {
          console.error("Error acquiring token interactively:", err);
          throw err;
        }
      } else {
        console.error("Error acquiring token silently:", error);
        throw error;
      }
    }
  };

  const value = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    getAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};